import { type FC, type ReactNode, useState } from "react"

import {
	type CommunityPostReaction,
	type CommunityPostReactionType,
	communityPostReactionLabels,
	communityPostReactionTypes,
	type PublicCommunityProfile,
} from "@forento/core/models/community"
import { toCountFormat } from "@forento/core/utilities/number"

import { cn } from "../utilities/tailwind"
import CommunityPostReactionIcon from "./CommunityPostReactionIcon"
import CommunityProfileAvatar from "./CommunityProfileAvatar"

type Props = {
	reactions: CommunityPostReaction[]
	labels?: Record<CommunityPostReactionType, string>
	allLabel?: string
	emptyLabel?: string
	renderAvatar?(profile: PublicCommunityProfile): ReactNode
}
const CommunityPostReactionList: FC<Props> = ({
	reactions,
	labels,
	allLabel = "All",
	emptyLabel = "No reactions yet.",
	renderAvatar,
}) => {
	const [selectedType, setSelectedType] = useState<CommunityPostReactionType | null>(null)

	const getLabel = (type: CommunityPostReactionType) => labels?.[type] ?? communityPostReactionLabels[type]

	const usedTypes = communityPostReactionTypes.filter(type => reactions.some(x => x.type === type))
	const filter = selectedType !== null && usedTypes.includes(selectedType) ? selectedType : null
	const visibleReactions = filter === null ? reactions : reactions.filter(x => x.type === filter)

	if (reactions.length === 0) return <p className="text-light">{emptyLabel}</p>

	return (
		<div className="flex flex-col gap-4">
			{usedTypes.length > 1 && (
				<div className="flex flex-wrap gap-1 border-b border-gray-200 pb-3">
					<Chip isSelected={filter === null} onClick={() => setSelectedType(null)}>
						{allLabel} {toCountFormat(reactions.length)}
					</Chip>
					{usedTypes.map(type => (
						<Chip
							key={type}
							isSelected={filter === type}
							title={getLabel(type)}
							onClick={() => setSelectedType(type)}
						>
							<CommunityPostReactionIcon type={type} size={16} />
							{toCountFormat(reactions.filter(x => x.type === type).length)}
						</Chip>
					))}
				</div>
			)}
			<ul className="flex max-h-96 flex-col gap-3 overflow-y-auto">
				{visibleReactions.map(reaction => (
					<li key={reaction.profile.id} className="flex items-center gap-3">
						<div className="relative shrink-0">
							{renderAvatar?.(reaction.profile) ?? (
								<CommunityProfileAvatar size={32} profile={reaction.profile} />
							)}
							<span
								role="img"
								aria-label={getLabel(reaction.type)}
								title={getLabel(reaction.type)}
								className="absolute -right-1 -bottom-1 rounded-full bg-white p-0.5 text-sm leading-none"
							>
								<CommunityPostReactionIcon type={reaction.type} size={14} />
							</span>
						</div>
						{reaction.profile.firstName} {reaction.profile.lastName}
					</li>
				))}
			</ul>
		</div>
	)
}

type ChipProps = { isSelected: boolean; title?: string; onClick(): void; children: ReactNode }
const Chip: FC<ChipProps> = ({ isSelected, title, onClick, children }) => (
	<button
		type="button"
		title={title}
		aria-label={title}
		aria-pressed={isSelected}
		onClick={onClick}
		className={cn(
			"flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 text-sm whitespace-nowrap",
			isSelected ? "bg-gray-100 font-semibold" : "hover:bg-gray-50",
		)}
	>
		{children}
	</button>
)

export default CommunityPostReactionList
